import { createFileRoute } from "@tanstack/react-router";
import { Lock, Download, FileCheck } from "lucide-react";
import { jainArt } from "@/components/jain-icon";
import { useEffect, useState } from "react";
import {
  listVisitTempleIds,
  countTemples,
  getProfile,
  getLatestCertificateForUser,
  getCertificateSettings,
  insertCertificate,
} from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { downloadCertificatePDF, type CertSettings } from "@/lib/certificate";

export const Route = createFileRoute("/app/certificate")({ component: CertScreen });

type Cert = {
  id: string;
  certificate_number: string;
  issued_at: string;
  visited_count: number;
  total_temples: number;
};

function CertScreen() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [visited, setVisited] = useState(0);
  const [total, setTotal] = useState(31);
  const [name, setName] = useState("Yatri");
  const [cert, setCert] = useState<Cert | null>(null);
  const [settings, setSettings] = useState<CertSettings | null>(null);
  const [issuing, setIssuing] = useState(false);

  const load = async () => {
    if (!user) {
      return;
    }
    const vs = listVisitTempleIds(user.id);
    const totalCt = countTemples();
    const pr = getProfile(user.id);
    const c = getLatestCertificateForUser(user.id);
    const s = getCertificateSettings();
    setVisited(vs.length);
    setTotal(totalCt);
    setName(pr?.full_name || "Yatri");
    setCert(c as Cert | null);
    setSettings(s as CertSettings | null);
  };

  useEffect(() => {
    load();
  }, [user]);

  const complete = visited >= total && total > 0;
  const pct = total ? Math.round((visited / total) * 100) : 0;

  const issue = async () => {
    if (!user || !complete) {
      return;
    }
    setIssuing(true);
    const data = insertCertificate({
      user_id: user.id,
      visited_count: visited,
      total_temples: total,
    });
    setIssuing(false);
    if (data) {
      setCert(data as Cert);
      download(data as Cert);
    }
  };

  const download = (c: Cert) => {
    if (!settings) {
      return;
    }
    downloadCertificatePDF({
      name,
      certificateNumber: c.certificate_number,
      issuedAt: c.issued_at,
      visited: c.visited_count,
      total: c.total_temples,
      lang,
      settings,
    });
  };

  return (
    <div className="px-5 pt-16">
      <div className="mb-3">
        <span className="font-serif text-2xl font-bold text-gradient">{t("certificate")}</span>
      </div>
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-5">
        <img
          src={jainArt.mandala}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 opacity-10 animate-spin-slow"
        />
        <div className="relative flex items-center gap-3">
          <img
            src={jainArt.kalash}
            alt=""
            aria-hidden
            className="h-14 w-14 object-contain animate-float"
          />
          <div>
            <div className="font-semibold">{t("progress")}</div>
            <div className="text-xs text-muted-foreground">
              {complete ? "🎉 Yatra complete!" : `Complete all ${total} temples to unlock`}
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="font-semibold">{t("visited")}</span>
          <span className="font-bold text-primary">
            {visited}/{total}
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-muted">
          <div
            className="h-2 rounded-full"
            style={{ background: "var(--gradient-saffron)", width: `${pct}%` }}
          />
        </div>
        {!complete ? (
          <button
            disabled
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-muted py-2.5 text-sm font-semibold text-muted-foreground"
          >
            <Lock className="h-4 w-4" /> {total - visited} more {t("pending")}
          </button>
        ) : cert ? (
          <button onClick={() => download(cert)} className="btn-primary mt-4 w-full py-2.5 text-sm">
            <Download className="h-4 w-4" /> Download Certificate
          </button>
        ) : (
          <button
            onClick={issue}
            disabled={issuing}
            className="btn-primary mt-4 w-full py-2.5 text-sm"
          >
            <FileCheck className="h-4 w-4" />{" "}
            {issuing ? "Issuing..." : "Issue & Download Certificate"}
          </button>
        )}
        {cert && (
          <p className="mt-2 text-center text-[10px] text-muted-foreground">
            No: {cert.certificate_number}
          </p>
        )}
      </div>

      <div
        className="mt-4 rounded-2xl border-2 border-dashed bg-card p-5 text-center"
        style={{ borderColor: "hsl(var(--saffron))" }}
      >
        <div
          className="relative overflow-hidden rounded-xl p-4"
          style={{ background: "hsl(var(--cream-deep))" }}
        >
          <img
            src={jainArt.mandala}
            alt=""
            aria-hidden
            className="pointer-events-none absolute -bottom-8 -left-8 h-32 w-32 opacity-15 animate-spin-slow"
          />
          <img
            src={jainArt.kalash}
            alt=""
            aria-hidden
            className="relative mx-auto h-14 w-14 object-contain"
          />
          <div className="relative mt-2 font-serif text-base font-bold text-gradient">
            JAIN GAURAV YATRA
          </div>
          <div className="text-[10px] uppercase text-muted-foreground">
            Certificate of {complete ? "Completion" : "Participation"}
          </div>
          <div className="my-3 h-px bg-border" />
          <div className="text-[10px] text-muted-foreground">This certifies that</div>
          <div className="font-serif text-lg font-bold text-foreground">{name}</div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            has visited{" "}
            <strong>
              {visited} of {total}
            </strong>{" "}
            Jain Derasars of Ahmedabad
          </div>
          <div className="mt-3 text-[10px] font-semibold">🙏 Jai Jinendra 🙏</div>
        </div>
      </div>
    </div>
  );
}
